// Campaign attribution for the website form. The landing URL carries the
// parameters, but the visitor may navigate (e.g. to /privacy) before submitting,
// so they are captured once on load and kept in memory. They are only written to
// sessionStorage after cookie consent, so nothing is stored on the device before that.
//
// These columns are also written by api/meta-leads-webhook.ts, but with different
// semantics: the website form stores the raw query string values a visitor
// arrived with, while the Lead Ads webhook resolves actual campaign/adset/ad names
// via the Graph API. Don't assume the two sources are directly comparable.

const STORAGE_KEY = 'leoTrackingParams';
const KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_adset',
  'utm_ad',
  'utm_placement',
  'gclid',
  'gbraid',
  'wbraid',
] as const;

type TrackingKey = (typeof KEYS)[number];
type TrackingValues = Partial<Record<TrackingKey, string>>;

let captured: TrackingValues = {};

function readStored(): TrackingValues {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as TrackingValues) : {};
  } catch {
    return {};
  }
}

// Call once on page load. A landing URL without campaign parameters never
// overwrites what an earlier page of the same visit captured.
export function captureTrackingParams() {
  const query = new URLSearchParams(window.location.search);
  const fresh: TrackingValues = {};
  for (const key of KEYS) {
    const value = query.get(key)?.trim().slice(0, 200);
    if (value) fresh[key] = value;
  }
  if (Object.keys(fresh).length > 0) captured = fresh;
}

// Call after the visitor accepted cookies.
export function persistTrackingParams() {
  const values = Object.keys(captured).length > 0 ? captured : readStored();
  if (Object.keys(values).length === 0) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(values));
  } catch {
    // Storage unavailable (private mode); the in-memory copy still works.
  }
}

export function getTrackingParams() {
  const values = Object.keys(captured).length > 0 ? captured : readStored();
  const isGoogleAds = Boolean(values.gclid || values.gbraid || values.wbraid);
  return {
    'UTM Source': values.utm_source ?? null,
    'UTM Medium': values.utm_medium ?? null,
    'UTM Content': values.utm_content ?? null,
    utm_campaign: values.utm_campaign ?? null,
    utm_adset: values.utm_adset ?? null,
    utm_ad: values.utm_ad ?? null,
    utm_placement: values.utm_placement ?? null,
    gclid: values.gclid ?? null,
    gbraid: values.gbraid ?? null,
    wbraid: values.wbraid ?? null,
    source: isGoogleAds ? 'google_ads' : 'website_form',
  };
}
